export const THEME_STORAGE_KEY = 'appTheme';
export const DEFAULT_THEME = 'light';
export const THEME_COLORS = { light: '#e1e4e8', dark: '#241e1a' };
export const THEME_SURFACE_COLORS = { light: '#eef0f2', dark: '#382c24' };

let currentTheme = null;
let initialized = false;
let listeningForStorage = false;
const listeners = new Set();
const themeColorOverrides = [];

const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

function normalizeTheme(value) {
  return value === 'dark' ? 'dark' : DEFAULT_THEME;
}

function readStoredTheme() {
  try {
    return normalizeTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return DEFAULT_THEME;
  }
}

function normalizeThemeColorPair(colors) {
  if (!colors || typeof colors !== 'object') return null;
  const light = typeof colors.light === 'string' ? colors.light.trim().toLowerCase() : '';
  const dark = typeof colors.dark === 'string' ? colors.dark.trim().toLowerCase() : '';
  return HEX_COLOR_PATTERN.test(light) && HEX_COLOR_PATTERN.test(dark) ? { light, dark } : null;
}

function getAppliedThemeColor(theme) {
  const activeOverride = themeColorOverrides[themeColorOverrides.length - 1];
  return activeOverride?.colors?.[theme] || THEME_COLORS[theme];
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  const appliedThemeColor = getAppliedThemeColor(theme);
  document.documentElement.style.setProperty('--app-status-bar-color', appliedThemeColor);
  // Current iOS Safari versions may derive browser chrome/overscroll tint from
  // the document surface instead of honoring a dynamically changed theme-color.
  // Keep every available signal synchronized for both Safari and installed PWAs.
  document.documentElement.style.backgroundColor = appliedThemeColor;
  if (document.body) document.body.style.backgroundColor = appliedThemeColor;
  let themeColor = document.querySelector('meta[name="theme-color"]');
  if (!themeColor && document.head) {
    themeColor = document.createElement('meta');
    themeColor.setAttribute('name', 'theme-color');
    document.head.appendChild(themeColor);
  }
  themeColor?.setAttribute('content', appliedThemeColor);
}

function updateTheme(theme) {
  const changed = currentTheme !== theme;
  currentTheme = theme;
  initialized = true;
  applyTheme(theme);
  if (changed) listeners.forEach((listener) => listener(theme));
  return theme;
}

function handleStorage(event) {
  if (event.key !== THEME_STORAGE_KEY && event.key !== null) return;
  try {
    if (event.storageArea && event.storageArea !== window.localStorage) return;
  } catch {
    // Storage may be unavailable in private/restricted browser contexts.
    return;
  }
  updateTheme(normalizeTheme(event.newValue));
}

function ensureStorageListener() {
  if (typeof window !== 'undefined' && !listeningForStorage) {
    window.addEventListener('storage', handleStorage);
    listeningForStorage = true;
  }
}

export function initializeTheme() {
  ensureStorageListener();
  return updateTheme(readStoredTheme());
}

export function getTheme() {
  return initialized ? currentTheme : readStoredTheme();
}

export function setTheme(value) {
  const theme = normalizeTheme(value);
  ensureStorageListener();
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Keep the current tab usable even when the preference cannot be persisted.
  }
  return updateTheme(theme);
}

export function subscribeToTheme(listener) {
  if (!initialized) initializeTheme();
  ensureStorageListener();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Temporarily replaces the browser/PWA status-area color for a mounted page.
 * Overrides are stacked so dialogs or nested full-screen routes can restore the
 * color that was active before they appeared.
 */
export function pushThemeColorOverride(colors) {
  const normalizedColors = normalizeThemeColorPair(colors);
  if (!normalizedColors) return () => {};

  const entry = { colors: normalizedColors };
  themeColorOverrides.push(entry);
  applyTheme(getTheme());

  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    const entryIndex = themeColorOverrides.indexOf(entry);
    if (entryIndex >= 0) themeColorOverrides.splice(entryIndex, 1);
    applyTheme(getTheme());
  };
}
